import type { ReactNode } from 'react'
import { TopNav } from '../landing/TopNav'
import { MarketingFooter } from '../landing/MarketingFooter'

// Shared layout for /login, /signup, /forgot-password (#7r2 expanded).
//
// Always renders: TopNav (sticky) → hero+card → [landingSections?] →
// MarketingFooter. Each page composes what's inside; Login passes
// landingSections to get the full marketing-equivalent landing, while
// Signup / ForgotPassword keep transactional flow only.
//
// Hero half (left column on md+):
//  - "Welcome to" (gold prefix)
//  - jnati wordmark image
//  - "Your Private Family Network" tagline (suppressed via hideTagline)
//  - sub-tagline (Login only, via subTagline prop)
//  - optional belowLogo slot (StepDots / StepIndicator)
//
// Card half (right column on md+): white form card with shadow.
//
// Centered both axes on md+ (audit r1 fix — r1 left-aligned within
// the column, drifted from marketing).

interface Props {
  /** Toggle the "Your Private Family Network" tagline. Default shown. */
  hideTagline?: boolean
  /** Optional descriptive sub-tagline below the main tagline (Login). */
  subTagline?: ReactNode
  /** Slot under the wordmark for step indicators. */
  belowLogo?: ReactNode
  /** White form card body. */
  children: ReactNode
  /** Link line below the card (e.g. "New to Jnati? Create an account"). */
  footer?: ReactNode
  /** Marketing content sections rendered below the hero+card, above
   *  the persistent MarketingFooter. Login-only. */
  landingSections?: ReactNode
}

export function AuthHeroLayout({
  hideTagline = false,
  subTagline,
  belowLogo,
  children,
  footer,
  landingSections,
}: Props) {
  return (
    <div className="min-h-screen bg-navy text-white flex flex-col">
      <TopNav />

      <section id="top" className="flex-1 flex items-stretch md:items-center px-4 py-10 md:py-16">
        <div className="w-full max-w-6xl mx-auto md:grid md:grid-cols-2 md:gap-10 lg:gap-16 md:items-center">
          {/* Hero half — centered both axes on md+ (#7r2 centering fix) */}
          <div className="flex flex-col items-center text-center mb-10 md:mb-0">
            <p className="text-gold text-base sm:text-lg font-semibold tracking-wide mb-2">Welcome to</p>
            <img
              src="/logo-wide.png"
              alt="jnati"
              className="h-20 sm:h-24 md:h-28 w-auto select-none"
              draggable={false}
            />
            {!hideTagline && (
              <p className="text-white text-base sm:text-lg font-semibold mt-4">
                Your Private Family Network
              </p>
            )}
            {subTagline && (
              <p className="text-white/75 text-sm sm:text-base mt-3 max-w-md leading-relaxed">
                {subTagline}
              </p>
            )}
            {belowLogo && <div className="mt-5 w-full flex justify-center">{belowLogo}</div>}
          </div>

          {/* Card half */}
          <div className="w-full max-w-md mx-auto md:mx-0">
            <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
              {children}
            </div>
            {footer && (
              <div className="text-center text-white/90 text-sm mt-5">
                {footer}
              </div>
            )}
          </div>
        </div>
      </section>

      {landingSections}

      <MarketingFooter />

      {/* Firebase RecaptchaVerifier mount point (#bug-pair-2). The
          singleton in src/config/firebase.ts targets containerId=
          'recaptcha-container'. Without this div mounted somewhere in
          the React tree, Firebase v9+ falls back to creating an
          invisible iframe on document.body — which works on most
          browsers but fails brittly on strict ones / first cold load
          with auth/argument-error, surfacing as "Failed to send OTP".
          Mounting the div in the shared auth layout fixes Signup,
          ForgotPassword, AddPhonePrompt, and any future OTP path in
          one place. Hidden via inline style (size:'invisible' anyway). */}
      <div id="recaptcha-container" style={{ display: 'none' }} />
    </div>
  )
}
